import { Status, type VirtualTableColumn } from "@snapchat/snap-design-system";

import { CopyToClipboardButton, InfoTooltip } from "../../../common/components";
import { type TagType } from "../../../common/components/TagWithTooltip";
import { Header } from "../../../common/components/table/Header";
import { TwoLineCell } from "../../../common/components/table/TwoLineCell";
import { DecreaseText } from "../../../common/components/text";
import { formatMediumDateTime } from "../../../common/formatters";
import { lensGroupIdIsCopiedToClipboard } from "../../../common/model";
import { type GroupsGridItemModel } from "../../grid/model";
import { GroupTags } from "./types";
import { HeaderWrapper } from "./GroupsTableColumns.styles";
import { LensesCell } from "./LensesCell";
import { PinCell } from "./PinCell";

// Unfortunately SDS doesn't export this interface
interface CellRenderer {
    row: {
        index: number;
    };
    data: GroupsGridItemModel[];
}

function getRenderer(fn: (group: GroupsGridItemModel) => JSX.Element) {
    return ({ row, data }: CellRenderer) => fn(data![row.index!] as GroupsGridItemModel);
}

function getGroupTags(group: GroupsGridItemModel): TagType<GroupTags>[] {
    if (group.isArchived) {
        return [{ text: GroupTags.REMOVED, status: Status.ERROR }];
    }
    if (group.readOnly) {
        return [{ text: GroupTags.READ_ONLY }];
    }
    return [];
}

const columnWidths = {
    pin: 34,
    name: 273,
    lastUpdated: 170,
    description: 250,
    lensCount: 100,
};

const minFirstLensesWidth = 700;

export function getColumns(
    onTogglePin: (group: GroupsGridItemModel, pin: boolean) => void,
    groupBeingPinned: string,
    width: number,
): VirtualTableColumn<GroupsGridItemModel>[] {
    const occupiedWidth = Object.values(columnWidths).reduce((w1, w2) => w1 + w2, 0);
    const firstLensesWidth = Math.max(minFirstLensesWidth, width - occupiedWidth);

    return [
        {
            id: "pin",
            width: columnWidths.pin,
            Cell: getRenderer((group) => (
                <PinCell
                    group={group}
                    pinned={group.isPinned ?? false}
                    disabled={!!groupBeingPinned}
                    isBeingPinned={groupBeingPinned === group.id}
                    onToggle={onTogglePin}
                />
            )),
        },
        {
            id: "name",
            accessor: "name",
            width: columnWidths.name,
            Header: <Header>Name / ID</Header>,
            Cell: getRenderer((group) => (
                <TwoLineCell text={group.name as string} subText={group.id as string} tags={getGroupTags(group)}>
                    <CopyToClipboardButton
                        copyValue={group.id!}
                        notification={lensGroupIdIsCopiedToClipboard}
                        tooltip="Copy Lens Group ID"
                    />
                </TwoLineCell>
            )),
        },
        {
            id: "lastUpdated",
            accessor: "lastUpdated",
            width: columnWidths.lastUpdated,
            Header: <Header>Last Updated</Header>,
            Cell: getRenderer((group) => <DecreaseText>{formatMediumDateTime(group.lastUpdated!)}</DecreaseText>),
        },
        {
            id: "description",
            accessor: "description",
            width: columnWidths.description,
            Header: <Header>Description</Header>,
            Cell: getRenderer((group) => <DecreaseText $wrap>{group.description}</DecreaseText>),
        },
        {
            id: "lensCount",
            width: columnWidths.lensCount,
            Header: <Header>Lenses</Header>,
            Cell: getRenderer((group) => <DecreaseText>{group.totalNumLenses} Lenses</DecreaseText>),
        },
        {
            id: "lenses",
            width: firstLensesWidth,
            Header: (
                <HeaderWrapper>
                    First 20 Lenses
                    <InfoTooltip content="These are the first 20 lenses in the group" />
                </HeaderWrapper>
            ),
            Cell: getRenderer((group) => <LensesCell lenses={group.lenses} totalNumLenses={group.totalNumLenses} />),
        },
    ];
}
